import { Button } from '@/components/atoms/NewPcButton';
import { DialogClose } from '@/components/atoms/Dialog';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import type { MembershipPaymentDialogMode } from '@/containers/payment/NewPaymentDialog';
import {
  getKakaoPayReadyInfo,
  type KakaoPayResponseInfo,
} from '@/pages/mobile-membership/mobile-membership-api';
import { WEB_PC_MEMBERSHIP_CHANNEL, type MembershipChannel } from '@/utils/pending-subscription';

interface NewPaymentDialogProps {
  isPrePayment?: boolean;
  subscriptionNo: number;
  paymentAmount: number;
  paymentChange?: boolean;
  mode?: MembershipPaymentDialogMode;
  pendingSubscriptionChannel?: MembershipChannel;
  onPendingSubscriptionSuccess?: () => void;
}

const ChangeBillKakao = ({
  isPrePayment,
  subscriptionNo,
  paymentAmount,
  paymentChange = false,
  mode = 'subscription',
  pendingSubscriptionChannel,
}: NewPaymentDialogProps) => {
  const { CplatToken } = useAppSelector((state) => state.user);
  const isPendingSubscriptionMode = mode === 'pendingSubscription';

  // 2. 카카오페이 결제 준비
  const { mutate: readyKakaoPay, isPending: isPayLoading } = usePost(
    CPLAT_API_ROUTES.paymentReadyForKakaoPay,
  );

  const handleKakaoPay = () => {
    if (!CplatToken) {
      alert('로그인이 필요합니다.');
      return;
    }
    if (!subscriptionNo) {
      alert('멤버십 정보가 없습니다.');
      return;
    }
    readyKakaoPay(
      {
        cplatToken: CplatToken,
        subscriptionNo,
        isPrePayment: isPendingSubscriptionMode ? true : isPrePayment,
        channel: pendingSubscriptionChannel ?? WEB_PC_MEMBERSHIP_CHANNEL,
      },
      {
        onSuccess: (res) => {
          const kakaoPayReadyInfo = getKakaoPayReadyInfo(res);
          const redirectUrl = kakaoPayReadyInfo
            ? getKakaoPayPcRedirectUrl(kakaoPayReadyInfo.kakaoPayResponseInfo)
            : undefined;

          if (!kakaoPayReadyInfo || !redirectUrl) {
            alert('카카오페이 결제 준비에 실패했습니다.');
            return;
          }

          localStorage.setItem(
            'kakaoPayRequestInfo',
            JSON.stringify(kakaoPayReadyInfo.kakaoPayRequestInfo),
          );
          localStorage.setItem(
            'kakaoPayResponseInfo',
            JSON.stringify(kakaoPayReadyInfo.kakaoPayResponseInfo),
          );
          localStorage.setItem('subscriptionNo', String(subscriptionNo));
          if (isPendingSubscriptionMode) {
            localStorage.setItem('kakaoPayFlow', 'pcMembershipPendingRegistration');
            localStorage.setItem(
              'kakaoPayPendingSubscriptionChannel',
              pendingSubscriptionChannel ?? WEB_PC_MEMBERSHIP_CHANNEL,
            );
          } else {
            localStorage.removeItem('kakaoPayFlow');
            localStorage.removeItem('kakaoPayPendingSubscriptionChannel');
          }
          if (paymentChange) localStorage.setItem('kakaoPayPaymentChange', 'true');
          else localStorage.removeItem('kakaoPayPaymentChange');
          window.location.href = redirectUrl;
        },
        onError: () => {
          alert('카카오페이 결제 준비에 실패했습니다.');
        },
      },
    );
  };

  return (
    <div className="flex w-full items-center justify-end gap-5">
      <div className="flex flex-col items-end">
        <div className="text-gr-900 Body1S16">
          {isPendingSubscriptionMode ? '결제 예정 금액' : '결제 금액'}{' '}
          {paymentAmount !== undefined ? paymentAmount.toLocaleString() : '-'}원
        </div>
        {(isPrePayment || isPendingSubscriptionMode) && (
          <div className="mt-[2px] text-gr-500 Caption1M12">
            {isPendingSubscriptionMode
              ? '등록된 결제수단으로 인앱 멤버십 종료 후 자동 결제됩니다'
              : '결제수단 등록 후 멤버십 정책에 따라 결제가 진행됩니다'}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <DialogClose asChild>
          <Button size="small" variant="line" className="h-[38px] shadow-none">
            취소
          </Button>
        </DialogClose>
        <Button
          size="small"
          isLoading={isPayLoading}
          onClick={handleKakaoPay}
          disabled={isPayLoading || !subscriptionNo}
          className="h-[38px]"
        >
          다음
        </Button>
      </div>
    </div>
  );
};

export default ChangeBillKakao;

const getKakaoPayPcRedirectUrl = (responseInfo: KakaoPayResponseInfo) => {
  const redirectUrl =
    responseInfo.nextRedirectPcUrl ??
    responseInfo.NextRedirectPcUrl ??
    responseInfo.nextRedirectMobileUrl ??
    responseInfo.NextRedirectMobileUrl;

  return typeof redirectUrl === 'string' ? redirectUrl : undefined;
};
